import { useState } from 'react';
import { ic } from '@/components/Icons';
import { Section } from '@/components/ui';
import { apiSource } from '@/data';
import { useI18n } from '@/i18n';
import { useAppStore } from '@/store/useAppStore';

/** The update window gate. While it is closed every non-admin role is
 *  read-only; the ETC Admin opens and closes it here.
 *
 *  Contra el backend real esto NO es una preferencia del navegador: la
 *  ventana vive en la base y la ven todos los socios a la vez. Por eso
 *  cada cambio va al servidor y solo se refleja en pantalla cuando el
 *  servidor confirma. Si falla, la pantalla se queda como estaba y lo
 *  dice: creer que cerraste la ventana cuando sigue abierta es
 *  exactamente el error que no se puede permitir aquí. */
export function WindowCard() {
  const { lang, t } = useI18n();
  const windowState = useAppStore((s) => s.windowState);
  const setWindowState = useAppStore((s) => s.setWindowState);
  const addToast = useAppStore((s) => s.addToast);
  const [guardando, setGuardando] = useState(false);

  async function aplicar(next: { open?: boolean; deadline?: string }, titulo: string) {
    const src = apiSource();
    if (!src) {
      // modo demostración: no hay servidor, se queda en el navegador
      setWindowState({ ...windowState, ...next } as typeof windowState);
      addToast({ kind: 'ok', title: titulo });
      return;
    }
    setGuardando(true);
    try {
      const w = await src.setWindow(next);
      setWindowState({ open: w.open, deadline: w.deadline ?? windowState.deadline });
      addToast({ kind: 'ok', title: titulo });
    } catch (err) {
      addToast({
        kind: 'err',
        title: lang === 'es' ? 'No se pudo cambiar el proceso' : 'Could not change the process',
        body: err instanceof Error ? err.message : String(err),
        details: lang === 'es'
          ? 'El proceso sigue como estaba. Vuelve a intentarlo.'
          : 'The process is unchanged. Try again.',
      });
    } finally {
      setGuardando(false);
    }
  }

  const toggle = () =>
    aplicar(
      { open: !windowState.open },
      !windowState.open
        ? lang === 'es' ? 'Proceso abierto' : 'Process opened'
        : lang === 'es' ? 'Proceso cerrado' : 'Process closed',
    );

  const cambiarFecha = (valor: string) => {
    // el campo de fecha dispara cambios a medio escribir; solo se manda
    // una fecha completa
    if (!/^\d{4}-\d{2}-\d{2}$/.test(valor)) return;
    void aplicar(
      { deadline: `${valor}T23:59:59` },
      lang === 'es' ? 'Fecha límite actualizada' : 'Deadline updated',
    );
  };

  const nextWindow = new Date(new Date(windowState.deadline).getTime() + 1000 * 60 * 60 * 24 * 180);

  return (
    <Section
      title={t('admin.window')}
      tone={windowState.open ? 'var(--ok)' : 'var(--st-lock)'}
      right={<span className={'chip ' + (windowState.open ? 'ok' : 'lock')}>{t(windowState.open ? 'window.status_open' : 'window.status_closed')}</span>}
    >
      <div className="m-grid1" style={{ marginTop: 8, display: 'grid', gridTemplateColumns: '1fr auto', gap: 14, alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{t('admin.window_help')}</div>
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)' }}>{t('admin.deadline')}</span>
              <input
                type="date"
                value={windowState.deadline.slice(0, 10)}
                disabled={guardando}
                onChange={(e) => cambiarFecha(e.target.value)}
                style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid var(--line-2)', fontSize: 13, background: 'var(--surface)' }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)' }}>{lang === 'es' ? 'Próximo proceso' : 'Next process'}</span>
              <span className="mono" style={{ fontSize: 13, paddingTop: 6 }}>
                {nextWindow.toLocaleDateString(lang === 'es' ? 'es-MX' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            </label>
          </div>
        </div>
        <button
          className={'btn ' + (windowState.open ? 'danger' : 'primary')}
          disabled={guardando}
          onClick={() => void toggle()}
        >
          {windowState.open ? ic.lock : ic.bell}
          {guardando
            ? (lang === 'es' ? 'Guardando…' : 'Saving…')
            : t(windowState.open ? 'window.admin_close' : 'window.admin_open')}
        </button>
      </div>
    </Section>
  );
}
